<script setup lang="ts">
import { computed, ref } from 'vue'

import { useLevelMetadata } from '../../composables/useLevelMetadata'
import { useMetricCatalog } from '../../composables/useMetricCatalog'
import { useReportSchedules } from '../../composables/useReportSchedules'
import { useSectionGroups } from '../../composables/useSectionGroups'
import { useSectionSelections } from '../../composables/useSectionSelections'
import type {
  ReportSectionConfig,
  ReportSectionTabConfig,
  SectionDataVisibleConfig,
} from '../../models/layoutConfig.types'
import { SECTION_GROUP_OVERALL } from '../../models/layoutConfig.types'
import { channelLabelKey } from '../../service/channelLabels'
import { resolveDataSyncEntries } from '../../service/dataSync'
import { resolveSectionFields, scheduleTabDetails } from '../../service/sectionFields'
import ReportChannelTabs from '../controls/ReportChannelTabs.vue'
import ReportToolbarSelect from '../controls/ReportToolbarSelect.vue'
import DataVisible from '../DataVisible.vue'

import ReportDatePicker from './ReportDatePicker.vue'
import ReportSectionTitle from './ReportSectionTitle.vue'
import SectionDropdowns from './SectionDropdowns.vue'

import { useI18n } from '@/shared/i18n'
import { SegmentedControl, type SelectOption } from '@/shared/ui'

// The v3 OrphexReportConfig orchestrator (656 ln), decomposed per ORP-18652: the selection
// cache lives in useSectionSelections, group/level sources in useSectionGroups, and config
// resolution is the section's `build` function — this SFC keeps only markup decisions.
// AddToDashboardModal is deliberately NOT here: module 09 embeds 08's composites, never the
// reverse. `pending` tabs (n-gram / dashboard-area renderers) are skipped until their
// renderer ports land.

const props = defineProps<{
  section: ReportSectionConfig
  /** Page-level date fragment; sections with their own picker override it locally. */
  defaultPostData: string
}>()

const { t, te } = useI18n()
const schedules = useReportSchedules()
const levels = useLevelMetadata()
const catalog = useMetricCatalog()

const renderableTabs = computed<ReportSectionTabConfig[]>(() =>
  props.section.tabs.filter((tab) => tab.kind !== 'pending'),
)

const selections = useSectionSelections({
  sectionId: () => props.section.id,
  tabs: renderableTabs,
  topTabs: () => props.section.topTabs,
  defaultLevel: () => props.section.defaultLevel,
})

const activeTab = computed(() => selections.selectedTab.value)

const groups = useSectionGroups({
  entityType: () => activeTab.value?.entityType ?? props.section.entityType,
  creativeLevel: () => props.section.creativeLevel,
})

// Section-local dates (v3 section `showDatePicker`); otherwise the page fragment flows down.
const localPostData = ref('')
const activePostData = computed(() =>
  props.section.showDatePicker ? localPostData.value : props.defaultPostData,
)

const title = computed(() => {
  const key = activeTab.value?.titleKey ?? props.section.titleKey
  return key ? t(key) : null
})
const description = computed(() => {
  const key = activeTab.value?.descriptionKey ?? props.section.descriptionKey
  return key ? t(key) : undefined
})

/** v3 topTabField mode: tabs render as a centered segmented control, no channel rail. */
const tabMode = computed<'top' | 'channels' | 'none'>(() => {
  if (props.section.topTabMode) return 'top'
  if (props.section.hideChannels || renderableTabs.value.length <= 1) return 'none'
  return 'channels'
})

const topTabOptions = computed<SelectOption<string>[]>(() =>
  renderableTabs.value.map((tab) => ({
    value: tab.name,
    label: tab.labelKey ? t(tab.labelKey) : tab.name,
  })),
)
const secondaryTopTabOptions = computed<SelectOption<string>[]>(() =>
  (props.section.topTabs ?? []).map((tab) => ({ value: tab.name, label: t(tab.labelKey) })),
)
const channelTabNames = computed(() => renderableTabs.value.map((tab) => tab.name))
/**
 * Channel-rail copy, resolved here so the rail never derives text from an identity (ORP-20215).
 * A tab's own `labelKey` wins — a layout registered by another feature owns its copy in its own
 * namespace — and `reports.channels.*` covers the platform names 08's seeded layouts use. A name
 * with neither is simply absent, and the rail renders the identity rather than inventing copy;
 * `channelLabelCoverage.spec` fails if a seeded layout ever leaves one uncovered.
 */
const channelLabels = computed<Record<string, string>>(() =>
  Object.fromEntries(
    renderableTabs.value.flatMap((tab) => {
      const key = tab.labelKey ?? channelLabelKey(tab.name)
      return te(key) ? [[tab.name, t(key)] as const] : []
    }),
  ),
)

const groupOptions = computed<SelectOption<string>[]>(() => [
  { value: SECTION_GROUP_OVERALL, label: t('reports.groups.overall') },
  ...groups.groupNames.value.map((name) => ({ value: name, label: name })),
])
const levelOptions = computed<SelectOption<string>[]>(() =>
  groups.levels.value.map((level) => ({ value: level, label: t(`reports.levels.${level}`) })),
)

const built = computed<SectionDataVisibleConfig | null>(() => {
  const tab = activeTab.value
  const build = props.section.build
  if (!tab || !build) return null
  return build({
    tab,
    dropdownFilters: selections.dropdownFilters(tab),
    level: selections.level.value,
    group: selections.group.value,
    topTab: selections.selectedTopTab.value,
  })
})

/**
 * The table column a selected custom group aggregates by (v3 `OrphexReportTable.
 * changeGroupColumns`: picking a group deselects every other dimension column and keeps only the
 * one matching its `slot_name`). "Overall" and a stale/unmatched name both fall back to null.
 */
const activeGroupField = computed(() => {
  const name = selections.group.value
  return name ? groups.fieldForGroup(name) : null
})

/** Build output + the schedule-metadata field bridge (see `service/sectionFields.ts`). */
const resolved = computed(() => {
  const config = built.value
  if (!config) return null
  const targetTabs = config.scheduleTabs ?? activeTab.value?.tabs ?? []
  // Every matched tab, not just the first: the offer universe is their column union (v3
  // `getPartitionColumns:290-305`). The pre-selected sets still seed off the first tab.
  const details = targetTabs.length
    ? scheduleTabDetails(schedules.checkScheduleTabs(config.reportId, targetTabs))
    : []
  // `showKeyEvents`/`hideFields` are resolved INTO `dimensions`/`metrics` here, so they must not
  // ride on to DataVisible as stray attrs — same reason `scheduleTabs` is peeled off.
  const {
    scheduleTabs: _scheduleTabs,
    showKeyEvents: _showKeyEvents,
    hideFields: _hideFields,
    ...rest
  } = config
  const fields = resolveSectionFields(config, details, { getColumn: catalog.getColumn })
  const runtimeMetrics = fields.partitionName
    ? levels.runtimeMetricFields(fields.partitionName)
    : []
  const groupField = activeGroupField.value
  const grouped = groupField && fields.availableFields.includes(groupField) ? groupField : null
  const dimensions = grouped ? [grouped] : fields.dimensions
  return {
    ...rest,
    dimensions,
    // A chart-table composition asks for the union of its table and chart dimensions, so a
    // section whose build hardcodes `chartDimensions` (e.g. budget allocation's `campaign_name`)
    // would keep that field in the request even though picking a group deselected it from the
    // table. The group is the surface's grouping — the chart breaks on it too.
    ...(grouped && rest.chartDimensions?.length ? { chartDimensions: [grouped] } : {}),
    metrics: fields.metrics,
    availableFields: [...new Set([...fields.availableFields, ...runtimeMetrics])],
    ...(fields.partitionName ? { partitionName: fields.partitionName } : {}),
  }
})

/** The active tab's carrying schedule — shared overflow maintenance payload target. */
const scheduleId = computed(() => {
  const reportId = built.value?.reportId ?? props.section.reportId
  return reportId === undefined ? null : (schedules.getScheduleByReportId(reportId)?.id ?? null)
})

/** Per-channel freshness stamps for the title tooltip (v3 OrphexAdditionalInfoTitle). */
const syncEntries = computed(() => {
  const reportId = built.value?.reportId ?? props.section.reportId
  if (reportId === undefined) return []
  const targetTabs = built.value?.scheduleTabs ?? activeTab.value?.tabs ?? []
  return resolveDataSyncEntries(
    schedules.getScheduleByReportId(reportId)?.additional_info,
    targetTabs,
  )
})

const stateLabel = computed(() => title.value ?? t('reports.section.fallbackLabel'))

function selectGroup(value: string | number | null | undefined): void {
  selections.group.value = value === SECTION_GROUP_OVERALL || value == null ? null : String(value)
}
</script>

<template>
  <section
    :id="section.id"
    :class="[
      channelTabNames.length < 2 || tabMode === 'top' ? 'p-4' : '',
      'report-section flex flex-col',
    ]"
  >
    <header
      v-if="title || section.showDatePicker"
      :class="[
        channelTabNames.length < 2 || tabMode === 'top' ? 'px-2' : 'px-4 mt-4',
        'report-section__header flex flex-wrap items-center justify-between gap-sm',
      ]"
    >
      <ReportSectionTitle
        v-if="title"
        :title="title"
        :description="description"
        :sync-entries="syncEntries"
      />
      <div class="flex w-max">
        <ReportDatePicker
          v-if="section.showDatePicker"
          :id="`report-section:${section.id}`"
          :default-preset="section.defaultPreset"
          :compare-default="section.compareDefault"
          :hidden-compare="section.hiddenCompare ?? false"
          @change="localPostData = $event"
        />
      </div>
    </header>

    <div v-if="tabMode === 'top'" class="report-section__top-tabs flex justify-center">
      <SegmentedControl
        :model-value="selections.selectedTabName.value"
        variant="filled"
        :label="t('reports.section.tabsLabel')"
        :options="topTabOptions"
        @update:model-value="(value) => (selections.selectedTabName.value = String(value))"
      />
    </div>
    <div v-if="section.topTabs?.length" class="report-section__top-tabs flex justify-center">
      <SegmentedControl
        :model-value="selections.selectedTopTabName.value"
        :label="t('reports.section.tabsLabel')"
        :options="secondaryTopTabOptions"
        @update:model-value="(value) => (selections.selectedTopTabName.value = String(value))"
      />
    </div>

    <div class="report-section__body flex min-w-0 items-stretch">
      <ReportChannelTabs
        v-if="tabMode === 'channels'"
        :model-value="selections.selectedTabName.value ?? ''"
        :tabs="channelTabNames"
        :labels="channelLabels"
        v-bind="{ ariaLabel: t('reports.section.tabsLabel') }"
        @update:model-value="(value) => (selections.selectedTabName.value = value)"
      />

      <div class="report-section__content min-w-0 flex-1">
        <component
          :is="section.renderer"
          v-if="section.renderer && resolved && activeTab"
          :config="{ ...resolved, scheduleTabs: built?.scheduleTabs ?? activeTab.tabs ?? [] }"
          :default-post-data="activePostData"
          v-bind="section.rendererProps ?? {}"
        >
          <!--
            A custom renderer gets the same section-level dropdown filter / group / level controls
            DataVisible receives below via its own `#controls` slot — passed THROUGH the renderer
            rather than drawn in a sibling row, so a renderer that renders its own controls (e.g.
            `OverviewChartWidget`'s Frequency/Platform) can put them in the SAME row instead of two
            disconnected ones. `rendererMode: 'prepend'` renders `DataVisible` too (below) and that
            copy owns the controls instead — this renderer gets none, matching the old gate exactly.
          -->
          <template
            v-if="
              section.rendererMode !== 'prepend' &&
              (activeTab?.dropdowns?.length || groups.hasGroups.value || levelOptions.length)
            "
            #controls
          >
            <SectionDropdowns
              v-if="activeTab?.dropdowns?.length"
              :tab="activeTab"
              :selection="(dropdown) => selections.dropdownIndex(activeTab!.name, dropdown)"
              @select="
                (dropdown, option) => selections.setDropdownIndex(activeTab!.name, dropdown, option)
              "
            />
            <ReportToolbarSelect
              v-if="groups.hasGroups.value"
              :label="t('reports.groups.label')"
              :options="groupOptions"
              :model-value="selections.group.value ?? SECTION_GROUP_OVERALL"
              icon="circle-nodes"
              @update:model-value="selectGroup"
            />
            <ReportToolbarSelect
              v-if="levelOptions.length"
              :label="t('reports.levels.label')"
              :options="levelOptions"
              :model-value="selections.level.value"
              icon="circle-nodes"
              @update:model-value="(value) => (selections.level.value = value as never)"
            />
          </template>
        </component>
        <DataVisible
          v-if="resolved && activeTab && (!section.renderer || section.rendererMode === 'prepend')"
          :key="`${section.id}:${activeTab.name}`"
          v-bind="{
            ...resolved,
            ariaLabel: stateLabel,
            emptyLabel: t('reports.section.empty'),
            errorLabel: t('reports.section.error'),
            retryLabel: t('reports.section.retry'),
          }"
          :default-post-data="activePostData"
          :maintenance-schedule-id="scheduleId"
        >
          <template #controls>
            <SectionDropdowns
              v-if="activeTab.dropdowns?.length"
              :tab="activeTab"
              :selection="(dropdown) => selections.dropdownIndex(activeTab!.name, dropdown)"
              @select="
                (dropdown, option) => selections.setDropdownIndex(activeTab!.name, dropdown, option)
              "
            />
            <ReportToolbarSelect
              v-if="groups.hasGroups.value"
              :label="t('reports.groups.label')"
              :options="groupOptions"
              :model-value="selections.group.value ?? SECTION_GROUP_OVERALL"
              icon="circle-nodes"
              @update:model-value="selectGroup"
            />
            <ReportToolbarSelect
              v-if="levelOptions.length"
              :label="t('reports.levels.label')"
              :options="levelOptions"
              :model-value="selections.level.value"
              icon="circle-nodes"
              @update:model-value="(value) => (selections.level.value = value as never)"
            />
          </template>
        </DataVisible>
      </div>
    </div>
  </section>
</template>
